import React, {createContext, Dispatch, SetStateAction, useEffect, useState} from 'react';
import moment from 'moment';
import {StateFilter} from '../../components/filter/GenericTableFilters';
import PaginationProvider from '../../components/table/PaginationContext';
import {Subscription} from '../../types/Subscription/Subscription';
import SubscriptionService from '../../services/SubscriptionService';
import {PageMetadata} from '../../types/Page';
import {Filter} from '../../types/Filter';

export const SubscriptionListDataContext = createContext<{
  subscriptions: Subscription[];
  filters?: {
    stateFilter: StateFilter[];
    setStateFilter: Dispatch<SetStateAction<StateFilter[]>>;
    dateStartFilter: string;
    setDateStartFilter: Dispatch<SetStateAction<string>>;
    dateEndFilter: string;
    setDateEndFilter: Dispatch<SetStateAction<string>>;
    searchFilter: string;
    setSearchFilter: Dispatch<SetStateAction<string>>;
    used: boolean;
  };
  page?: PageMetadata;
  reload: () => void;
  loading: boolean;
  isPrefilteredForCustomer: boolean;
  currentPage: { set: (page: number) => void; get: number };
  pageSize: { set: (size: number) => void; get: number };
}>({
  subscriptions: [] as Subscription[],
  reload: () => {},
  loading: true,
  isPrefilteredForCustomer: false,
  currentPage: {
    set: (page) => {},
    get: 0,
  },
  pageSize: {
    set: (size) => {},
    get: 0,
  },
});

type SubscriptionListDataContextProviderProps = {
  pageSizeOptions: number[];
  children: any;
  customerId?: string;
  setSubscriptionsCount?: (setSubscriptionsCount: number) => void;
  hasPaginationProvider?: boolean;
};

const SubscriptionListDataContextProvider = ({
  pageSizeOptions,
  customerId,
  children,
  setSubscriptionsCount,
  hasPaginationProvider = true,
}: SubscriptionListDataContextProviderProps) => {
  const [subscriptionList, setSubscriptionList] = useState<Subscription[]>([] as Subscription[]);
  const [page, setPage] = useState<PageMetadata>();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [stateFilter, setStateFilter] = useState<StateFilter[]>([] as StateFilter[]);
  const [dateStartFilter, setDateStartFilter] = useState<string>('');
  const [dateEndFilter, setDateEndFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [filtersUsed, setFiltersUsed] = useState(false);

  const isInitialized = React.useRef(false);

  useEffect(() => {
    if (isInitialized.current) {
      reload();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (isInitialized.current) {
      if (currentPage !== 0) {
        setCurrentPage(0);
      } else {
        reload();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter, dateStartFilter, dateEndFilter, searchFilter, pageSize]);

  useEffect(() => {
    reload();
    isInitialized.current = true;
    return () => {
      isInitialized.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = () => {
    setLoading(true);
    const filter: Filter = {
      customerRef: customerId,
    };
    if (!!stateFilter && stateFilter.length !== 0) {
      filter.statuses = Array.from(stateFilter.values()).join(',');
    }

    filter.search = searchFilter;
    filter.startDate = dateStartFilter && moment(dateStartFilter).format('YYYY-MM-DDT23:59:59.999') + 'Z';
    filter.endDate = dateEndFilter && moment(dateEndFilter).format('YYYY-MM-DDT23:59:59.999') + 'Z';

    setFiltersUsed((!!stateFilter && stateFilter.length !== 0) || !!dateStartFilter || !!dateEndFilter || !!searchFilter);

    SubscriptionService.getSubscriptions(pageSize, currentPage || 0, 'desc', filter).then(({ results, page }) => {
      setSubscriptionsCount && setSubscriptionsCount(page.totalElements);
      setSubscriptionList(results);
      setPage(page);
      setLoading(false);
    });
  };

  return (
    <SubscriptionListDataContext.Provider
      value={{
        subscriptions: subscriptionList,
        filters: {
          stateFilter: stateFilter,
          setStateFilter: setStateFilter,
          dateStartFilter: dateStartFilter,
          setDateStartFilter: setDateStartFilter,
          dateEndFilter: dateEndFilter,
          setDateEndFilter: setDateEndFilter,
          searchFilter: searchFilter,
          setSearchFilter: setSearchFilter,
          used: filtersUsed,
        },
        page: page,
        reload: reload,
        loading: loading,
        isPrefilteredForCustomer: !!customerId,
        currentPage: { set: setCurrentPage, get: currentPage },
        pageSize: { set: setPageSize, get: pageSize },
      }}
    >
      {hasPaginationProvider ? (
        <PaginationProvider
          pageSizeOptions={pageSizeOptions}
          currentPage={currentPage || 0}
          totalPages={page?.totalPages || 0}
          pageSize={pageSize}
          setCurrentPage={setCurrentPage}
          changePageSize={setPageSize}
        >
          {children}
        </PaginationProvider>
      ) : (
        children
      )}
    </SubscriptionListDataContext.Provider>
  );
};

export default SubscriptionListDataContextProvider;
